import { twMerge } from 'tailwind-merge'

import useTranslation from 'next-translate/useTranslation'
import MedianSoldPriceInfo from './components/median-sold-price-info'
import MedianForSalePriceInfo from './components/median-for-sale-price-info'
import {
  DistanceUnit,
  Place,
  PropertyListingStatus,
  ResidentialPropertyListingComparablesResponse,
} from '@unreserved-frontend-v2/api/generated/graphql/types'
import { Box } from '@unreserved-frontend-v2/ui/box/box'
import { FilterSelections } from '@unreserved-frontend-v2/ui/schema-table/models'
import { ListSideMedianSkeleton } from '@unreserved-frontend-v2/ui/skeleton/list-side-median-skeleton'
import { formatMinMaxDisplayValues } from '@unreserved-frontend-v2/utils/format-min-max-display-values'
import { GeoInputForUI } from '@unreserved-frontend-v2/geolocation/types'
import { stringAddressToObject } from '@unreserved-frontend-v2/geolocation/components/geosearch-filter/utils'

export interface ComparablesInfoBoxProps {
  data?: ResidentialPropertyListingComparablesResponse
  filterSelections: FilterSelections
  isFetching: boolean
  place?: Partial<Place> | null
  className?: string
}

export function ComparablesInfoBox({ className, isFetching, ...inheritedProps }: ComparablesInfoBoxProps) {
  const { t } = useTranslation()

  // Return skeleton for fetching in-widget
  if (isFetching) {
    return (
      <Box className="p-5">
        <ListSideMedianSkeleton />
      </Box>
    )
  }

  const { filterSelections, place, data } = inheritedProps

  const isViewingSold = filterSelections.listingStatuses === PropertyListingStatus.Sold
  const geo = filterSelections.geo as GeoInputForUI

  const placeTitle =
    geo?.distance && geo?.displayAddress
      ? t('listings:comparables-info.within-distance-of-place', {
          distance: `${geo.distance.value} ${geo.distance.unit === DistanceUnit.Kilometers ? 'km' : 'm'}`,
          address:
            geo.displayAddress === t('geolocation:location-selector.current-location')
              ? geo.displayAddress.toLowerCase()
              : stringAddressToObject(geo.displayAddress)?.address1,
        })
      : place?.nameLong || t('listings:comparables-info.canada')

  // Formatting filters to display in any title
  const displayedFilters = [
    // Square Footage filter
    formatMinMaxDisplayValues(
      filterSelections['squareFootageGTE'] !== 'ALL'
        ? t('listings:sqft', { sqft: filterSelections['squareFootageGTE'] as string })
        : undefined,
      filterSelections['squareFootageLTE'] !== 'ALL'
        ? t('listings:sqft', { sqft: filterSelections['squareFootageLTE'] as string })
        : undefined,
      '',
      t('listings:sqft', { sqft: '' })
    ),
    // Bedrooms filter
    filterSelections['totalBedroomsGTE'] !== 'ALL'
      ? t('listings:comparables-info.beds', { count: `${filterSelections['totalBedroomsGTE']}+` })
      : undefined,
    // Bathrooms filter
    filterSelections['totalBathroomsGTE'] !== 'ALL'
      ? t('listings:comparables-info.baths', { count: `${filterSelections['totalBathroomsGTE']}+` })
      : undefined,
  ]
    .filter(Boolean)
    .join(', ')

  return data?.soldListingsCount || data?.totalListingsCount ? (
    <Box className={twMerge('flex flex-col gap-1.5 px-5 pb-5 pt-1', className)} data-testid="comparables-info">
      {
        // If viewing sold listings, reverse the order of rendering, else render as is
        (!isViewingSold
          ? [MedianForSalePriceInfo, MedianSoldPriceInfo]
          : [MedianSoldPriceInfo, MedianForSalePriceInfo]
        ).map((Component, i) => {
          return (
            <Component
              {...inheritedProps}
              displayedFilters={displayedFilters}
              isViewingSold={isViewingSold}
              placeTitle={placeTitle as string}
              key={Component.name}
            />
          )
        })
      }
    </Box>
  ) : null
}

export default ComparablesInfoBox
